import React from "react";
import {
  Box,
  Text,
  SimpleGrid,
  Skeleton,
  createStyles,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconClock,
  IconHeartbeat,
  IconChecklist,
  IconArrowRight,
  IconListCheck,
} from "@tabler/icons-react";

const useStyles = createStyles((theme) => ({
  tile: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "20px",
    border: "1px solid #EAECF0",
    cursor: "pointer",
    transition: "box-shadow 150ms ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    },
  },
  tileHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 12,
  },
  tileIcon: {
    width: 40,
    height: 40,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tileCount: {
    fontSize: 28,
    fontWeight: 700,
    fontFamily: "Inter",
    color: "#101828",
    lineHeight: 1.2,
  },
  tileLabel: {
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "Inter",
    color: "#667085",
    marginTop: 4,
  },
  trendUp: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#F04438",
  },
  trendDown: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#12B76A",
  },
  trendFlat: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#667085",
  },
  percentageTile: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "20px",
    border: "1px solid #EAECF0",
    cursor: "pointer",
    transition: "box-shadow 150ms ease",
    "&:hover": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    },
  },
  cta: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 500,
    fontFamily: "Inter",
    color: "#7F56D9",
    marginTop: 10,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const TILE_CONFIG = [
  {
    key: "criticalAlerts",
    label: "Critical Alerts",
    icon: IconAlertTriangle,
    iconBg: "#FEF3F2",
    iconColor: "#F04438",
    invertTrend: true,
  },
  {
    key: "overdueTasks",
    label: "Overdue Staff Tasks",
    icon: IconListCheck,
    iconBg: "#FFF6ED",
    iconColor: "#F79009",
  },
  {
    key: "overduePendingNotes",
    label: "Pending Notes (> 3 Days)",
    icon: IconClock,
    iconBg: "#FFF6ED",
    iconColor: "#F79009",
  },
  {
    key: "missingCarePlans",
    label: "Missing Care Plans",
    icon: IconHeartbeat,
    iconBg: "#FEF3F2",
    iconColor: "#F04438",
  },
];

const PERCENTAGE_TILES = [
  {
    key: "marCompletion",
    label: "MAR Completion",
    valueField: "percentage",
    prevField: "prevPercentage",
    suffix: "%",
  },
];

const ComplianceSnapshot = ({ data, loading, onTileClick }) => {
  const { classes } = useStyles();

  if (loading) {
    return (
      <SimpleGrid
        cols={5}
        spacing="md"
        breakpoints={[
          { maxWidth: 900, cols: 2 },
          { maxWidth: 600, cols: 1 },
        ]}
      >
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={120} radius={12} />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid
      cols={5}
      spacing="md"
      breakpoints={[
        { maxWidth: 900, cols: 2 },
        { maxWidth: 600, cols: 1 },
      ]}
    >
      {TILE_CONFIG.map((tile) => {
        const tileData = data?.[tile.key] || {};
        const Icon = tile.icon;

        return (
          <Box
            key={tile.key}
            className={classes.tile}
            onClick={() => onTileClick?.(tile.key)}
          >
            <Box className={classes.tileHeader}>
              <Box
                className={classes.tileIcon}
                sx={{ backgroundColor: tile.iconBg }}
              >
                <Icon size={20} color={tile.iconColor} />
              </Box>
            </Box>
            <Text className={classes.tileCount}>
              {tileData.count ?? 0}
            </Text>
            <Text className={classes.tileLabel}>{tile.label}</Text>
            <Text className={classes.cta}>
              See more <IconArrowRight size={14} />
            </Text>
          </Box>
        );
      })}

      {PERCENTAGE_TILES.map((tile) => {
        const tileData = data?.[tile.key] || {};

        return (
          <Box
            key={tile.key}
            className={classes.percentageTile}
            onClick={() => onTileClick?.(tile.key)}
          >
            <Box className={classes.tileHeader}>
              <Box
                className={classes.tileIcon}
                sx={{ backgroundColor: "#ECFDF3" }}
              >
                <IconChecklist size={20} color="#12B76A" />
              </Box>
            </Box>
            <Text className={classes.tileCount}>
              {tileData[tile.valueField] != null
                ? `${Number(tileData[tile.valueField]).toFixed(1)}${tile.suffix}`
                : "--"}
            </Text>
            <Text className={classes.tileLabel}>{tile.label}</Text>
            <Text className={classes.cta}>
              See more <IconArrowRight size={14} />
            </Text>
          </Box>
        );
      })}
    </SimpleGrid>
  );
};

export default ComplianceSnapshot;
